/**
 * Location Context (ENH-004, ENH-005)
 * Manages delivery location and delivery window state
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import * as shopApi from '../services/shopApi';
import { getErrorMessage } from '../utils/errorHandler';

// ============================================
// TYPES
// ============================================

interface DeliveryWindow {
  is_same_day: boolean;
  delivery_date: string;
  window_start: string;
  window_end: string;
  display_text: string;
  countdown_seconds: number | null;
  cutoff_time: string | null;
}

interface ETABreakdown {
  fulfillment: number;
  drive_time: number;
  batch_buffer: number;
  distance_miles: number;
  minutes_since_order: number | null;
  batch_urgency: 'normal' | 'soon' | 'urgent';
}

interface DeliveryInfo {
  is_eligible: boolean;
  zip_code: string;
  distance_miles: number | null;
  shipping_cost: string | null;
  eta_min: number | null;
  eta_max: number | null;
  eta_display: string | null;
  eta_breakdown?: ETABreakdown | null;  // Optional, included when coordinates provided
  delivery_window: DeliveryWindow | null;
  message: string;
}

interface DeliveryStatus {
  current_time: string;
  timezone: string;
  operating_hours: {
    start: string;
    end: string;
    display: string;
  };
  is_open: boolean;
  same_day_cutoff: string;
  is_same_day_eligible: boolean;
  delivery_window: DeliveryWindow;
  next_open: string | null;
}

interface LocationState {
  zipCode: string | null;
  deliveryInfo: DeliveryInfo | null;
  deliveryStatus: DeliveryStatus | null;
  isLoading: boolean;
  error: string | null;
}

interface LocationContextValue extends LocationState {
  setZipCode: (zip: string) => Promise<void>;
  clearLocation: () => void;
  refreshDeliveryInfo: () => Promise<void>;
  refreshDeliveryStatus: () => Promise<void>;
  isLocationModalOpen: boolean;
  openLocationModal: () => void;
  closeLocationModal: () => void;
  hasSeenLocationPrompt: boolean;
  markLocationPromptSeen: () => void;
}

// ============================================
// CONTEXT
// ============================================

const STORAGE_KEY = 'arkx_delivery_location';
const PROMPT_SEEN_KEY = 'arkx_location_prompt_seen';

const LocationContext = createContext<LocationContextValue | undefined>(undefined);

export function LocationProvider({ children }: { children: React.ReactNode }) {
  const [zipCode, setZipCodeState] = useState<string | null>(null);
  const [deliveryInfo, setDeliveryInfo] = useState<DeliveryInfo | null>(null);
  const [deliveryStatus, setDeliveryStatus] = useState<DeliveryStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [hasSeenLocationPrompt, setHasSeenLocationPrompt] = useState(true); // Default true to prevent flash

  // Load from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    const promptSeen = localStorage.getItem(PROMPT_SEEN_KEY);

    // Check if they've seen the prompt before
    setHasSeenLocationPrompt(!!promptSeen || !!stored);

    if (stored) {
      try {
        const { zipCode: storedZip, timestamp } = JSON.parse(stored);
        // Only use if less than 24 hours old
        if (Date.now() - timestamp < 24 * 60 * 60 * 1000) {
          // Silently set zip and fetch info
          setZipCodeState(storedZip);
          fetchDeliveryInfo(storedZip);
        } else {
          localStorage.removeItem(STORAGE_KEY);
        }
      } catch (e) {
        console.error('Failed to parse stored location:', e);
        localStorage.removeItem(STORAGE_KEY);
      }
    }

    // Fetch delivery status on mount
    fetchDeliveryStatus();
  }, []);

  // Refresh delivery status periodically (every 5 minutes)
  useEffect(() => {
    const interval = setInterval(() => {
      fetchDeliveryStatus();
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  const fetchDeliveryInfo = async (zip: string) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await shopApi.getDeliveryInfo(zip);
      const info = response.data;

      setDeliveryInfo(info);
      setError(info.is_eligible ? null : info.message);

      return info;
    } catch (err: any) {
      console.error('Failed to fetch delivery info:', err);
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDeliveryStatus = async () => {
    try {
      const response = await shopApi.getDeliveryStatus();
      setDeliveryStatus(response.data);
    } catch (err: any) {
      console.error('Failed to fetch delivery status:', err);
      // Don't set error for status failures - it's non-critical
    }
  };

  const setZipCode = useCallback(async (zip: string): Promise<void> => {
    setZipCodeState(zip);

    try {
      await fetchDeliveryInfo(zip);

      // Persist to localStorage
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        zipCode: zip,
        timestamp: Date.now(),
      }));
    } catch (err) {
      throw err;
    }
  }, []);

  const clearLocation = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setZipCodeState(null);
    setDeliveryInfo(null);
    setError(null);
  }, []);

  const refreshDeliveryInfo = useCallback(async () => {
    if (zipCode) {
      await fetchDeliveryInfo(zipCode);
    }
  }, [zipCode]);

  const refreshDeliveryStatus = useCallback(async () => {
    await fetchDeliveryStatus();
  }, []);

  const openLocationModal = useCallback(() => {
    setIsLocationModalOpen(true);
  }, []);

  const closeLocationModal = useCallback(() => {
    setIsLocationModalOpen(false);
  }, []);

  const markLocationPromptSeen = useCallback(() => {
    localStorage.setItem(PROMPT_SEEN_KEY, 'true');
    setHasSeenLocationPrompt(true);
  }, []);

  const value = useMemo<LocationContextValue>(() => ({
    zipCode,
    deliveryInfo,
    deliveryStatus,
    isLoading,
    error,
    setZipCode,
    clearLocation,
    refreshDeliveryInfo,
    refreshDeliveryStatus,
    isLocationModalOpen,
    openLocationModal,
    hasSeenLocationPrompt,
    markLocationPromptSeen,
    closeLocationModal,
  }), [
    zipCode, deliveryInfo, deliveryStatus, isLoading, error,
    setZipCode, clearLocation, refreshDeliveryInfo, refreshDeliveryStatus,
    isLocationModalOpen, openLocationModal, hasSeenLocationPrompt,
    markLocationPromptSeen, closeLocationModal
  ]);

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
}

// ============================================
// HOOK
// ============================================

export function useLocation() {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within LocationProvider');
  }
  return context;
}

// ============================================
// UTILITY HOOKS
// ============================================

/**
 * Hook to get just the delivery window info (lightweight)
 */
export function useDeliveryWindow() {
  const { deliveryInfo, deliveryStatus } = useLocation();

  // Prefer delivery info window if we have a valid location
  if (deliveryInfo?.is_eligible && deliveryInfo.delivery_window) {
    return deliveryInfo.delivery_window;
  }

  // Fall back to general delivery status
  return deliveryStatus?.delivery_window || null;
}

/**
 * Hook to check if same-day delivery is available
 */
export function useSameDayDelivery() {
  const { deliveryInfo, deliveryStatus } = useLocation();

  // If we have location, use that
  if (deliveryInfo?.is_eligible) {
    return deliveryInfo.delivery_window?.is_same_day || false;
  }

  // Otherwise use general status
  return deliveryStatus?.is_same_day_eligible || false;
}

/**
 * Hook to get shipping cost for current location
 */
export function useShippingCost() {
  const { deliveryInfo } = useLocation();

  if (deliveryInfo?.is_eligible) {
    return deliveryInfo.shipping_cost;
  }

  return null;
}

/**
 * Hook to get ETA display for current location
 */
export function useDeliveryETA() {
  const { deliveryInfo } = useLocation();

  if (deliveryInfo?.is_eligible) {
    return deliveryInfo.eta_display;
  }

  return null;
}

export default LocationContext;
